import { useEffect, useState } from "react";
import { Check, RotateCcw, Save, Shield, ShieldAlert, Sparkles } from "lucide-react";
import Button from "@/components/ui/Button";
import { useToast } from "@/components/ui/Toast";
import { adminService } from "@/services/apiServices";
import { usePermission } from "@/context/PermissionContext";

interface RoleDef {
  key: string;
  label: string;
  description: string;
}

interface ModuleDef {
  key: string;
  label: string;
  description: string;
}

interface ActionDef {
  key: "view" | "create" | "edit" | "approve" | "delete";
  short: string;
  label: string;
  color: string;
}

const DEFAULT_ROLES: RoleDef[] = [
  { key: "super_admin", label: "SUPER ADMIN", description: "Full unrestricted system access" },
  { key: "admin", label: "ADMIN", description: "Operations and retailer manager" },
  { key: "retailer_onboarding_executive", label: "Retailer onboarding executive", description: "Retailer onboarding" },
  { key: "retailer_onboarding_executive_walletrequesthandler", label: "Retailer Onboarding Executive & Wallet request handler", description: "Retailer onboarding and handling wallet request" },
  { key: "wallet_request_handler", label: "Wallet request handler", description: "Wallet top-up approval & finance logs" },
];

const DEFAULT_MODULES: ModuleDef[] = [
  { key: "dashboard", label: "Dashboard overview", description: "Operational metrics, volume trends & quick cards" },
  { key: "mandates", label: "Mandate master", description: "Customer e-mandates, auth status & detailed ledger" },
  { key: "installments", label: "Installment schedule", description: "EMI collections, presentment & bounce retries" },
  { key: "retailers", label: "Retailer directory", description: "Customer records, profiles & merchant activation" },
  { key: "wallets", label: "Wallet & top-ups", description: "Retailer balance, top-up request approval & ledger" },
  { key: "reports", label: "Reports & registers", description: "Statements, volume analytics & ledger exports" },
  { key: "settings", label: "Platform & access settings", description: "Business settings, staff users, rates & RBAC matrix" },
  { key: "logs", label: "Audit log trail", description: "Read-only security events & transaction activity trail" },

];

const ACTIONS: ActionDef[] = [
  { key: "view", short: "V", label: "View", color: "bg-slate-100 text-slate-700 border-slate-300 hover:bg-slate-200" },
  { key: "create", short: "C", label: "Create", color: "bg-blue-100 text-blue-700 border-blue-300 hover:bg-blue-200" },
  { key: "edit", short: "E", label: "Edit", color: "bg-sky-100 text-sky-700 border-sky-300 hover:bg-sky-200" },
  { key: "approve", short: "A", label: "Approve", color: "bg-emerald-100 text-emerald-700 border-emerald-300 hover:bg-emerald-200" },
  { key: "delete", short: "D", label: "Delete", color: "bg-rose-100 text-rose-700 border-rose-300 hover:bg-rose-200" },
];

export default function RbacMatrixTab() {
  const toast = useToast();
  const { isSuperAdmin, refreshPermissions } = usePermission();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const [roles, setRoles] = useState<RoleDef[]>(DEFAULT_ROLES);
  const [modules, setModules] = useState<ModuleDef[]>(DEFAULT_MODULES);
  const [matrix, setMatrix] = useState<Record<string, Record<string, Record<string, boolean>>>>({});

  const loadMatrix = async () => {
    setLoading(true);
    try {
      const res = await adminService.getRoleMatrix();
      if (res && res.data) {
        if (res.data.roles) setRoles(res.data.roles);
        if (res.data.modules) setModules(res.data.modules);
        if (res.data.matrix) setMatrix(res.data.matrix);
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to load RBAC matrix");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMatrix();
  }, []);

  const togglePermission = (roleKey: string, moduleKey: string, actionKey: string) => {
    if (roleKey === "super_admin") {
      toast.info("Super Admin possesses unrestricted privileges by system design.");
      return;
    }

    setMatrix((prev) => {
      const roleCopy = { ...(prev[roleKey] || {}) };
      const modCopy = { ...(roleCopy[moduleKey] || { view: false, create: false, edit: false, approve: false, delete: false }) };
      modCopy[actionKey] = !modCopy[actionKey];

      // Auto-enable view if creating, editing, approving, or deleting
      if (modCopy[actionKey] && actionKey !== "view") {
        modCopy.view = true;
      }

      // If view is disabled, disable all other action privileges for that module
      if (actionKey === "view" && !modCopy.view) {
        modCopy.create = false;
        modCopy.edit = false;
        modCopy.approve = false;
        modCopy.delete = false;
      }

      roleCopy[moduleKey] = modCopy;
      return { ...prev, [roleKey]: roleCopy };
    });

    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminService.updateRoleMatrix(matrix);
      await refreshPermissions();
      setHasChanges(false);
      toast.success("RBAC permissions matrix successfully saved!");
    } catch (err: any) {
      toast.error(err.message || "Failed to save permissions matrix");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Are you sure you want to reset all role permissions to the factory default configuration?")) {
      return;
    }

    setSaving(true);
    try {
      const res = await adminService.resetRoleMatrix();
      if (res && res.data && res.data.matrix) {
        setMatrix(res.data.matrix);
      } else {
        await loadMatrix();
      }
      await refreshPermissions();
      setHasChanges(false);
      toast.success("Role permissions matrix restored to default values.");
    } catch (err: any) {
      toast.error(err.message || "Failed to reset permissions");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center rounded-[10px] border border-line bg-card p-6 shadow-card">
        <div className="flex items-center gap-3 text-ink-secondary">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary-500 border-t-transparent" />
          <span className="text-sm font-medium">Loading RBAC permissions matrix...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Top Banner & Control Bar */}
      <div className="flex flex-wrap items-center justify-between gap-4 rounded-[10px] border border-line bg-card p-5 shadow-card">
        <div>
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-50 text-primary-500">
              <Shield className="h-4 w-4" />
            </div>
            <h2 className="text-lg font-semibold text-ink">Role-Based Access Control (RBAC) Matrix</h2>
          </div>
          <p className="mt-1 text-xs text-ink-secondary">
            Configure module visibility and operational capabilities for each staff role. Click any permission pill to toggle.
          </p>
        </div>

        <div className="flex items-center gap-3">
          {hasChanges && (
            <span className="flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700 border border-amber-200">
              <span className="h-2 w-2 rounded-full bg-amber-500 animate-pulse" />
              Unsaved changes
            </span>
          )}

          <Button
            variant="secondary"
            onClick={handleReset}
            disabled={saving}
            leftIcon={<RotateCcw className="h-3.5 w-3.5" />}
          >
            Reset Defaults
          </Button>

          <Button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            leftIcon={<Save className="h-3.5 w-3.5" />}
          >
            {saving ? "Saving..." : "Save Matrix"}
          </Button>
        </div>
      </div>

      {/* Main RBAC Grid matching Reference Image */}
      <div className="overflow-hidden rounded-[10px] border border-line bg-card shadow-card">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm border-collapse">
            <thead>
              <tr className="border-b border-line bg-slate-50/75">
                <th className="py-3.5 px-5 font-semibold text-xs text-ink-secondary tracking-wider uppercase min-w-[240px]">
                  MODULE
                </th>
                {roles.map((role) => (
                  <th
                    key={role.key}
                    className="py-3.5 px-3 font-semibold text-xs text-ink-secondary tracking-wider uppercase text-center min-w-[180px]"
                  >
                    <div className="flex flex-col items-center">
                      <span className="text-ink font-semibold">{role.label}</span>
                      <span className="text-[10px] font-normal text-ink-secondary capitalize mt-0.5">
                        {role.key === "super_admin" ? "Master Access" : role.description.slice(0, 24) + "..."}
                      </span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {modules.map((mod) => (
                <tr key={mod.key} className="hover:bg-slate-50/50 transition-colors">
                  {/* Module Title & Description */}
                  <td className="py-4 px-5 align-middle">
                    <p className="font-semibold text-ink text-sm capitalize">{mod.label}</p>
                    <p className="text-xs text-ink-secondary mt-0.5 leading-snug">{mod.description}</p>
                  </td>

                  {/* Role Columns with Pills */}
                  {roles.map((role) => {
                    const isSuper = role.key === "super_admin";
                    const rolePerms = matrix[role.key]?.[mod.key] || {
                      view: isSuper,
                      create: isSuper,
                      edit: isSuper,
                      approve: isSuper,
                      delete: isSuper,
                    };

                    return (
                      <td key={role.key} className="py-4 px-2 align-middle text-center">
                        <div className="flex items-center justify-center gap-1.5 flex-nowrap">
                          {ACTIONS.map((act) => {
                            const isGranted = isSuper ? true : Boolean(rolePerms[act.key]);

                            return (
                              <button
                                key={act.key}
                                type="button"
                                disabled={isSuper}
                                onClick={() => togglePermission(role.key, mod.key, act.key)}
                                title={`${act.label} permission on ${mod.label} for ${role.label}`}
                                className={`flex h-7 w-7 items-center justify-center rounded-md border text-xs font-semibold transition-all select-none cursor-pointer ${
                                  isGranted
                                    ? act.color + " shadow-xs ring-1 ring-black/5"
                                    : "bg-white/80 text-slate-300 border-dashed border-slate-200 hover:border-slate-400 hover:text-slate-600"
                                } ${isSuper ? "cursor-default opacity-95" : "active:scale-95"}`}
                              >
                                {act.short}
                              </button>
                            );
                          })}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer Legend matching Reference Image */}
        <div className="flex flex-wrap items-center justify-between gap-4 border-t border-line bg-slate-50/50 px-5 py-3.5">
          <div className="flex items-center gap-3 text-xs">
            <span className="font-medium text-ink-secondary mr-1">Legend:</span>
            {ACTIONS.map((act) => (
              <div key={act.key} className="flex items-center gap-1.5">
                <span className={`flex h-5 w-5 items-center justify-center rounded border text-[11px] font-bold ${act.color}`}>
                  {act.short}
                </span>
                <span className="text-ink-secondary">{act.label}</span>
              </div>
            ))}
          </div>

          <div className="text-xs text-ink-secondary flex items-center gap-2">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" />
            <span>{roles.length} roles actively enforced across all routes</span>
          </div>
        </div>
      </div>
    </div>
  );
}
